import { Injectable } from '@angular/core';
import { InterviewGuideReminder } from '../../core-data-model/InterviewGuideReminder.model';
import { JobStub } from '../../core-data-model/Job/JobStub.model';
import { OiLink } from '../../core-data-model/OiLink.model';
import {
    OrderedSet,
    StandardMaxOrderedSetSize
} from '../../core-data-model/OrderedSet';
import {
    UserProfileSetting,
    CompanySpecificSetting,
    OnboardingChecklist
} from '../../core-data-model/UserProfileSetting.model';
import {
    IUserProfileSettingServer,
    CompanySpecificSettingServer,
    OldOnboardingChecklist
} from '../../core-data-model/UserProfileSetting.serverinterface';
import { Util } from '../../core-utilities/Util';
import { Logger } from '../../system-services/Logger/Logger.service';

@Injectable({
    providedIn: 'root'
})
export class UserProfileSettingTransformer {
    constructor(
        private util: Util,
        private logger: Logger
    ) {}

    // Handle outdated data from the server version
    // TODO: Take version into account
    handleOldServerToClient(
        potentiallyOldStyleSettingServer: CompanySpecificSettingServer,
        companySetting: CompanySpecificSetting
    ) {
        /*
         *  RENAMES
         */
        const serverCompanyFoldersArray =
            potentiallyOldStyleSettingServer.recentlySelectedFolders
                ? potentiallyOldStyleSettingServer.recentlySelectedFolders
                      .orderedArray
                : potentiallyOldStyleSettingServer.recentlySelectedFolderIds
                      .orderedArray;
        companySetting.recentlySelectedFolderIds = new OrderedSet<string>(
            serverCompanyFoldersArray ? serverCompanyFoldersArray : [],
            StandardMaxOrderedSetSize + 1,
            StandardMaxOrderedSetSize
        );

        const serverCompanyGroupsArray =
            potentiallyOldStyleSettingServer.recentlySelectedGroups
                ? potentiallyOldStyleSettingServer.recentlySelectedGroups
                      .orderedArray
                : potentiallyOldStyleSettingServer.recentlySelectedGroupIds
                      .orderedArray;
        companySetting.recentlySelectedGroupIds = new OrderedSet<string>(
            serverCompanyGroupsArray ? serverCompanyGroupsArray : [],
            StandardMaxOrderedSetSize + 1,
            StandardMaxOrderedSetSize
        );

        /*
         *  DATA ALTERATIONS
         */
        // Please note, job stubs came in in place of jobs, but we were conveniently storing the exact same suboject
    }

    serverToClient(
        userProfileSettingServerJSON: IUserProfileSettingServer
    ): UserProfileSetting {
        const setting = new UserProfileSetting();
        try {
            if (userProfileSettingServerJSON) {
                setting.currentCompany =
                    userProfileSettingServerJSON.currentCompany;
                setting.version = userProfileSettingServerJSON.version;
                setting.onboarding = !userProfileSettingServerJSON.onboarding
                    ? 'unstarted'
                    : userProfileSettingServerJSON.onboarding;
                // this feels clunky...
                setting.showCognitiveData =
                    userProfileSettingServerJSON.showCognitiveData;
                setting.hasAcknowledgedCogBestPractices =
                    userProfileSettingServerJSON.hasAcknowledgedCogBestPractices;
                setting.completedCardAdministeredByMe =
                    userProfileSettingServerJSON.completedCardAdministeredByMe;
                setting.pendingCardAdministeredByMe =
                    userProfileSettingServerJSON.pendingCardAdministeredByMe;
                setting.jobCardModifiedByMe =
                    userProfileSettingServerJSON.jobCardModifiedByMe;
                setting.preferredPageSize =
                    userProfileSettingServerJSON.preferredPageSize;
                setting.tableColumnSettings =
                    userProfileSettingServerJSON.tableColumnSettings;
                setting.hasAcknowledgedWelcomeGuideCta =
                    userProfileSettingServerJSON.hasAcknowledgedWelcomeGuideCta;
                setting.myOiLink = userProfileSettingServerJSON.myOiLink
                    ? new OiLink({
                          jobId: userProfileSettingServerJSON.myOiLink.jobId,
                          folderId:
                              userProfileSettingServerJSON.myOiLink.folderId,
                          personType:
                              userProfileSettingServerJSON.myOiLink.personType,
                          autoSendReport:
                              userProfileSettingServerJSON.myOiLink
                                  .autoSendReport,
                          sendCogAfterBA:
                              userProfileSettingServerJSON.myOiLink
                                  .sendCogAfterBA === 'true'
                      })
                    : null;
                setting.reminders = userProfileSettingServerJSON.reminders
                    ? userProfileSettingServerJSON.reminders.map(
                          (reminder) =>
                              new InterviewGuideReminder({
                                  companyId: reminder.companyId,
                                  personId: reminder.personId,
                                  jobId: reminder.jobId,
                                  date: this.util.newDate(reminder.date)
                              })
                      )
                    : null;

                setting.firstLogin = this.util.newDate(
                    userProfileSettingServerJSON.firstLogin
                );

                if (userProfileSettingServerJSON.onboardingChecklist) {
                    if (
                        this.typeGuardIsOnboardingChecklist(
                            userProfileSettingServerJSON.onboardingChecklist
                        )
                    ) {
                        setting.onboardingChecklist =
                            userProfileSettingServerJSON.onboardingChecklist;
                        if (
                            userProfileSettingServerJSON.onboardingChecklist
                                .dropdownOpen === undefined
                        ) {
                            setting.onboardingChecklist.dropdownOpen = true;
                        } else {
                            setting.onboardingChecklist.dropdownOpen =
                                userProfileSettingServerJSON.onboardingChecklist.dropdownOpen;
                        }
                    } else {
                        setting.onboardingChecklist.connectBehavioral =
                            userProfileSettingServerJSON.onboardingChecklist.connectBehavioral;
                        setting.onboardingChecklist.initialLogin =
                            userProfileSettingServerJSON.onboardingChecklist.initialLogin;
                        setting.onboardingChecklist.inviteEmployeesBA =
                            userProfileSettingServerJSON.onboardingChecklist.inviteEmployeesBA;

                        if (
                            userProfileSettingServerJSON.onboardingChecklist
                                .addMoreUsers
                        ) {
                            setting.onboardingChecklist.addMoreUsers = 'done';
                        } else {
                            setting.onboardingChecklist.addMoreUsers =
                                'notDone';
                        }
                        setting.onboardingChecklist.dropdownOpen = true;
                    }
                } else {
                    userProfileSettingServerJSON.onboardingChecklist = {
                        initialLogin: true,
                        connectBehavioral: false,
                        inviteEmployeesBA: false,
                        addMoreUsers: 'notDone',
                        dropdownOpen: true
                    };
                }
                setting.onboardingNextSteps =
                    !userProfileSettingServerJSON.onboardingNextSteps
                        ? {
                              personSnapshot: false,
                              personalPlacard: false,
                              pdc: false,
                              relationshipGuide: false,
                              teamPlacard: false,
                              teamWorkstyles: false
                          }
                        : userProfileSettingServerJSON.onboardingNextSteps;

                const interviewReminderLastShown =
                    userProfileSettingServerJSON.interviewReminderLastShown;
                setting.interviewReminderLastShown = interviewReminderLastShown
                    ? new Date(parseInt(interviewReminderLastShown, 10))
                    : null;
                setting.freemiumFirstCandidateViewed =
                    userProfileSettingServerJSON.freemiumFirstCandidateViewed;
                setting.hireSettings =
                    userProfileSettingServerJSON.hireSettings;
                setting.aiAssistantUserSettings =
                    userProfileSettingServerJSON.aiAssistantUserSettings;
                if (userProfileSettingServerJSON.companySpecificSettings) {
                    const companySpecificSettings =
                        userProfileSettingServerJSON.companySpecificSettings.orderedArray.map(
                            (companySettingServer) => {
                                const companySetting =
                                    new CompanySpecificSetting();
                                companySetting.companyId =
                                    companySettingServer.companyId;

                                this.handleOldServerToClient(
                                    companySettingServer,
                                    companySetting
                                );
                                companySetting.recentlySelectedJobs =
                                    companySettingServer.recentlySelectedJobs
                                        ? new OrderedSet<JobStub>(
                                              companySettingServer.recentlySelectedJobs.orderedArray.map(
                                                  (jobStub) =>
                                                      new JobStub({
                                                          id: jobStub.id,
                                                          title: jobStub.title
                                                      })
                                              ),
                                              StandardMaxOrderedSetSize + 1,
                                              StandardMaxOrderedSetSize
                                          )
                                        : new OrderedSet<JobStub>(
                                              [],
                                              StandardMaxOrderedSetSize + 1,
                                              StandardMaxOrderedSetSize
                                          );
                                return companySetting;
                            }
                        );
                    setting.companySpecificSettings =
                        new OrderedSet<CompanySpecificSetting>(
                            companySpecificSettings
                        );
                } else {
                    setting.companySpecificSettings =
                        new OrderedSet<CompanySpecificSetting>([]);
                }
            }
        } catch (err) {
            this.logger.logError(
                'UserProfileSettings - Bad Data',
                userProfileSettingServerJSON
            );
        }

        return setting;
    }

    clientToServer(
        userProfileSetting: UserProfileSetting
    ): IUserProfileSettingServer {
        if (userProfileSetting) {
            let companySettings: CompanySpecificSettingServer[] = [];
            companySettings =
                userProfileSetting.companySpecificSettings.orderedArray.map(
                    (companySpecificSetting) => {
                        const companySpecificSettingServer =
                            new CompanySpecificSettingServer();
                        companySpecificSettingServer.companyId =
                            companySpecificSetting.companyId;

                        companySpecificSettingServer.recentlySelectedFolderIds =
                            new OrderedSet<string>(
                                companySpecificSetting.recentlySelectedFolderIds.orderedArray,
                                StandardMaxOrderedSetSize + 1,
                                StandardMaxOrderedSetSize
                            );
                        companySpecificSettingServer.recentlySelectedGroupIds =
                            new OrderedSet<string>(
                                companySpecificSetting.recentlySelectedGroupIds.orderedArray,
                                StandardMaxOrderedSetSize + 1,
                                StandardMaxOrderedSetSize
                            );
                        companySpecificSettingServer.recentlySelectedJobs =
                            new OrderedSet<{
                                id: string;
                                title: string;
                            }>(
                                companySpecificSetting.recentlySelectedJobs.orderedArray.map(
                                    (job) => ({
                                        id: job.id,
                                        title: job.title
                                    })
                                ),
                                StandardMaxOrderedSetSize + 1,
                                StandardMaxOrderedSetSize
                            );
                        return companySpecificSettingServer;
                    }
                );

            const userProfileSettingServer: IUserProfileSettingServer = {
                version: userProfileSetting.version,
                currentCompany: userProfileSetting.currentCompany,
                companySpecificSettings:
                    new OrderedSet<CompanySpecificSettingServer>(
                        companySettings
                    ),
                showCognitiveData: userProfileSetting.showCognitiveData,
                hasAcknowledgedCogBestPractices:
                    userProfileSetting.hasAcknowledgedCogBestPractices,
                completedCardAdministeredByMe:
                    userProfileSetting.completedCardAdministeredByMe,
                pendingCardAdministeredByMe:
                    userProfileSetting.pendingCardAdministeredByMe,
                jobCardModifiedByMe: userProfileSetting.jobCardModifiedByMe,
                preferredPageSize: userProfileSetting.preferredPageSize,
                tableColumnSettings: userProfileSetting.tableColumnSettings,
                myOiLink: userProfileSetting.myOiLink
                    ? {
                          jobId: userProfileSetting.myOiLink.jobId,
                          folderId: userProfileSetting.myOiLink.folderId,
                          personType: userProfileSetting.myOiLink.personType,
                          autoSendReport:
                              userProfileSetting.myOiLink.autoSendReport,
                          sendCogAfterBA:
                              userProfileSetting.myOiLink.sendCogAfterBA + ''
                      }
                    : null,
                interviewReminderLastShown:
                    userProfileSetting.interviewReminderLastShown &&
                    userProfileSetting.interviewReminderLastShown
                        .getTime()
                        .toString(),
                reminders: userProfileSetting.reminders
                    ? userProfileSetting.reminders
                          .filter((reminder) => !reminder.completed)
                          .map((reminder: InterviewGuideReminder) => ({
                              companyId: reminder.companyId,
                              personId: reminder.personId,
                              jobId: reminder.jobId,
                              date: reminder.date,
                              completed: reminder.completed
                          }))
                    : null,
                onboarding: userProfileSetting.onboarding,
                firstLogin: userProfileSetting.firstLogin
                    ? userProfileSetting.firstLogin.toDateString()
                    : null,
                onboardingChecklist: userProfileSetting.onboardingChecklist,
                onboardingNextSteps: userProfileSetting.onboardingNextSteps,
                freemiumFirstCandidateViewed:
                    userProfileSetting.freemiumFirstCandidateViewed,
                hireSettings: userProfileSetting.hireSettings,
                hasAcknowledgedWelcomeGuideCta:
                    userProfileSetting.hasAcknowledgedWelcomeGuideCta,
                aiAssistantUserSettings:
                    userProfileSetting.aiAssistantUserSettings
                        ? userProfileSetting.aiAssistantUserSettings
                        : null
            };
            return userProfileSettingServer;
        }
        return;
    }

    typeGuardIsOnboardingChecklist(
        obj: OldOnboardingChecklist | OnboardingChecklist
    ): obj is OnboardingChecklist {
        return typeof (obj as OnboardingChecklist).addMoreUsers === 'string';
    }
}
